import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AUTO_FILL_PATTERNS } from '../../../../shared/config/autoFillPatterns';
import { WorkspaceStatusPill } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import {
  getKeyLibraryColumnHeaderSx,
  getKeyLibraryCompactFieldSx,
  getKeyLibraryPaginationSx,
  getKeyLibraryReadOnlyCellSx,
  getKeyLibraryTableContainerSx,
  getKeyLibraryTableSurfaceSx,
  getKeyLibraryTableSx,
  getKeyLibraryToolbarSx,
} from './quickFillKeyLibraryStyles';

export const PIPEEX_BUILT_IN_KEY_COUNT = AUTO_FILL_PATTERNS.length;

const HeaderCell: React.FC<{ label: string; align?: 'left' | 'right' }> = ({
  label,
  align = 'left',
}) => (
  <TableCell align={align}>
    <Box sx={getKeyLibraryColumnHeaderSx}>
      <Typography component="span" data-header-label>{label}</Typography>
    </Box>
  </TableCell>
);

const builtInColumns = [
  { id: 'rule', label: 'Rule', width: '11.5rem', align: 'left' as const },
  { id: 'keys', label: 'Matching keys', width: '28rem', align: 'left' as const },
  { id: 'objectType', label: 'OBJECT-TYPE', width: '9rem', align: 'left' as const },
  { id: 'skey', label: 'SKEY', width: '6rem', align: 'left' as const },
  { id: 'endType', label: 'END-TYPE', width: '10rem', align: 'left' as const },
  { id: 'category', label: 'Category', width: '7rem', align: 'left' as const },
  { id: 'priority', label: 'Priority', width: '5rem', align: 'right' as const },
] as const;

const ReadOnlyCell: React.FC<React.PropsWithChildren<{ align?: 'left' | 'right' }>> = ({
  align = 'left',
  children,
}) => (
  <Box
    sx={(theme) => ({
      ...getKeyLibraryReadOnlyCellSx(theme),
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    })}
  >
    {children}
  </Box>
);

const QuickFillBuiltInKeyLibraryTable: React.FC = () => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const filteredPatterns = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return AUTO_FILL_PATTERNS
      .filter((pattern) => !normalizedQuery || [
        pattern.id,
        pattern.objectType,
        pattern.skey,
        pattern.endType ?? '',
        pattern.category,
        ...pattern.keywords,
      ].join(' ').toLocaleLowerCase().includes(normalizedQuery))
      .sort((left, right) =>
        left.category.localeCompare(right.category) ||
        left.id.localeCompare(right.id)
      );
  }, [query]);

  const lastPage = Math.max(0, Math.ceil(filteredPatterns.length / rowsPerPage) - 1);
  const visiblePage = Math.min(page, lastPage);
  const visiblePatterns = filteredPatterns.slice(
    visiblePage * rowsPerPage,
    visiblePage * rowsPerPage + rowsPerPage
  );

  return (
    <Stack spacing={0} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        sx={getKeyLibraryToolbarSx}
      >
        <TextField
          label="Search PipeEX dictionary"
          size="small"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(0);
          }}
          sx={(theme) => getKeyLibraryCompactFieldSx(theme, 280)}
        />
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <WorkspaceStatusPill
            label={`${filteredPatterns.length} ${filteredPatterns.length === 1 ? 'rule' : 'rules'} shown`}
            tone={filteredPatterns.length ? 'success' : 'neutral'}
            size="compact"
          />
          <Typography variant="caption" color="text.secondary">
            PipeEX rules are read-only; local entries cover unmatched families.
          </Typography>
        </Stack>
      </Stack>
      {filteredPatterns.length === 0 ? (
        <Alert severity="info">No PipeEX dictionary entries match this search.</Alert>
      ) : (
        <Paper elevation={0} sx={getKeyLibraryTableSurfaceSx}>
          <TableContainer sx={getKeyLibraryTableContainerSx}>
            <Table
              stickyHeader
              size="small"
              aria-label="PipeEX auto-fill dictionary"
              data-workspace-grid="quick-fill"
              sx={getKeyLibraryTableSx}
            >
              <colgroup>
                {builtInColumns.map((column) => (
                  <col key={column.id} style={{ width: column.width }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {builtInColumns.map((column) => (
                    <HeaderCell key={column.id} label={column.label} align={column.align} />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visiblePatterns.map((pattern) => (
                  <TableRow key={pattern.id} hover>
                    <TableCell><ReadOnlyCell><Box sx={{ fontWeight: 650 }}>{pattern.id}</Box></ReadOnlyCell></TableCell>
                    <TableCell>
                      <Tooltip title={pattern.keywords.join(', ')} placement="top" enterDelay={500}>
                        <Box sx={{ minWidth: 0 }}>
                          <ReadOnlyCell>{pattern.keywords.join(', ')}</ReadOnlyCell>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell><ReadOnlyCell>{pattern.objectType}</ReadOnlyCell></TableCell>
                    <TableCell><ReadOnlyCell>{pattern.skey}</ReadOnlyCell></TableCell>
                    <TableCell><ReadOnlyCell>{pattern.endType ?? 'Resolved by SKEY'}</ReadOnlyCell></TableCell>
                    <TableCell><ReadOnlyCell>{pattern.category}</ReadOnlyCell></TableCell>
                    <TableCell><ReadOnlyCell align="right">{pattern.priority}</ReadOnlyCell></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPatterns.length}
            page={visiblePage}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            sx={getKeyLibraryPaginationSx}
          />
        </Paper>
      )}
    </Stack>
  );
};

export default QuickFillBuiltInKeyLibraryTable;
